import React from 'react';

import { DataTable, type TableColumn } from '@/src/ui';
import type { ClassMatrixQuestionDetailsDTO } from '../models/classMatrixDTOs';
import { Constants, ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import { QuestionRenderer } from '@/src/lib';

const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

type QuestionDetailsResponsesTableProps = {
    data: ClassMatrixQuestionDetailsDTO;
    openKey: string;
    setOpenKey: React.Dispatch<React.SetStateAction<string>>;
};

export default function QuestionDetailsResponsesTable({
    data,
    openKey,
    setOpenKey,
}: QuestionDetailsResponsesTableProps) {
    const isChoiceType =
        data.questionTypeMasterId === qtm.MULTIPLE_CHOICE ||
        data.questionTypeMasterId === qtm.MULTIPLE_SELECT;

    function displayForNonChoice(isCorrect?: boolean | null) {
        if (isCorrect === true) return 'Correct';
        if (isCorrect === false) return 'Incorrect';
        return 'Unanswered';
    }

    return (
        <div id="classMatrixQuestionDetails" className="space-y-3">
            {data.buckets.length === 0 && (
                <div className="text-sm text-gray-500">No responses yet.</div>
            )}

            {data.buckets.length > 0 && (
                <div className="rounded-lg overflow-auto border border-gray-300 bg-white">
                    <button
                        type="button"
                        onClick={() => setOpenKey((prev) => (prev === 'all' ? '' : 'all'))}
                        className="w-full flex justify-between px-3 py-2"
                        style={{ background: 'var(--pm-gray)', border: 'none', outline: 'none' }}
                        aria-expanded={openKey === 'all'}
                    >
                        <span className="text-sm font-medium text-gray-800">Student Responses</span>
                        <span className="text-xs text-gray-700">
                            {openKey === 'all' ? 'Hide' : 'Show'} Responses
                        </span>
                    </button>

                    {openKey === 'all' && (
                        <div className="px-3 py-2 bg-white border-gray-100">
                            <DataTable
                                tableName="QuestionDetails_UniqueResponses"
                                rowsPerPageInitial={20}
                                alternatingRowColor
                                defaultSortColumn="displayResponse"
                                defaultSortDirection="asc"
                                data={(data.buckets ?? []).map((b) => {
                                    const correctness = b?.isCorrect;
                                    return {
                                        label: b.label,
                                        students: b.students,
                                        count: b.count,
                                        isCorrect: correctness,
                                        displayResponse: isChoiceType
                                            ? b.label
                                            : displayForNonChoice(correctness),
                                        rendererUserResponse: b.rendererUserResponse as
                                            | string
                                            | undefined,
                                    };
                                })}
                                rowSx={(row) => ({
                                    backgroundColor:
                                        row.isCorrect === true
                                            ? '#D1FADF'
                                            : row.isCorrect === false
                                              ? '#FEE4E2'
                                              : undefined,
                                })}
                                columns={
                                    [
                                        {
                                            dataKey: 'displayResponse',
                                            header: 'Response',
                                            align: 'left',
                                            allowSort: true,
                                            emptyValue: '-',
                                            headerSx: ReportHeaderStyle,
                                            cellSx: ReportCellStyle,
                                            formatter: (row: any) => (
                                                <div className="flex flex-col gap-2">
                                                    {row.rendererUserResponse ? (
                                                        <QuestionRenderer
                                                            question={data.questionData}
                                                            loadedUserResponse={row.label}
                                                            stimMode="sev"
                                                            options={{
                                                                hidePrompt: true,
                                                                disabled: true,
                                                                language: 'en',
                                                            }}
                                                        />
                                                    ) : (
                                                        '(Unanswered)'
                                                    )}
                                                </div>
                                            ),
                                        },
                                        {
                                            dataKey: 'students',
                                            header: 'Students',
                                            align: 'left',
                                            emptyValue: '-',
                                            headerSx: ReportHeaderStyle,
                                            cellSx: ReportCellStyle,
                                            formatter: (row: any) => {
                                                const students = (row.students || []) as Array<{
                                                    name: string;
                                                    studentId: number;
                                                }>;
                                                if (!students.length) {
                                                    return (
                                                        <span className="text-gray-500 text-sm text-center">
                                                            —
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <div className="flex flex-col gap-1">
                                                        {students.map((s) => (
                                                            <div
                                                                key={s.studentId}
                                                                className="text-sm text-gray-800 text-center"
                                                            >
                                                                {s.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            dataKey: 'count',
                                            header: 'Count',
                                            align: 'right',
                                            allowSort: true,
                                            emptyValue: '-',
                                            headerSx: ReportHeaderStyle,
                                            cellSx: ReportCellStyle,
                                            formatter: (row: any) => (
                                                <div className="text-center">{row.count ?? 0}</div>
                                            ),
                                        },
                                    ] as TableColumn[]
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
